import React from "react";
import type { SimpleFieldConfig, FieldValue } from "../types/index";
import type { ContactMethodEntityContext } from "@/lib/utils/crm/contact-method-context";
import { ContactMethodActions } from "@/components/shared/ContactMethodActions/ContactMethodActions";

/**
 * Renders phone / email cell values as Hub-native actions:
 *
 *   phone → opens the Send Text Message modal (with optional Call in Quo)
 *   email → opens the compose email modal
 *
 * Detection is by the field's configured `icon` ("Phone" / "Mail") with a fieldId
 * regex and an email-shaped-value fallback, because phone/email fields are declared
 * as `type: "text"` (no dedicated field type). Returns null when the value is not a
 * contact method so the caller falls through to scalar rendering.
 *
 * Every control calls `stopPropagation` on click so tapping it opens the modal;
 * tapping elsewhere in the cell/card still opens the detail view.
 */

const EMAIL_VALUE_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const PHONE_FIELD_ID_RE = /(^|_)(phone|mobile|cell|fax|tel)(_|$)/i;
const EMAIL_FIELD_ID_RE = /(^|_)email(_|$)/i;

export function renderContactMethodCell(
  value: FieldValue,
  fieldConfig: SimpleFieldConfig,
  fieldId: string,
  entityContext?: ContactMethodEntityContext
): React.ReactNode | null {
  if (value === null || value === undefined) return null;

  const str = String(value).trim();
  if (!str) return null;

  const icon = fieldConfig?.icon;
  const isEmail =
    icon === "Mail" || EMAIL_FIELD_ID_RE.test(fieldId) || EMAIL_VALUE_RE.test(str);
  const isPhone = icon === "Phone" || PHONE_FIELD_ID_RE.test(fieldId);

  if (isEmail && !isPhone) {
    return (
      <ContactMethodActions kind="email" value={str} entityContext={entityContext} />
    );
  }

  if (isPhone) {
    return (
      <ContactMethodActions kind="phone" value={str} entityContext={entityContext} />
    );
  }

  return null;
}
